import React, { useEffect, useState } from "react";
import { X, Info, AlertCircle, CheckCircle2, AlertTriangle, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type B24NotificationVariant = "info" | "success" | "warning" | "error";

interface B24NotificationProps {
  title: string;
  message: string;
  variant?: B24NotificationVariant;
  icon?: LucideIcon;
  duration?: number;
  onClose?: () => void;
  className?: string;
  /** Inline "Undo" affordance for reversible actions (e.g. "Rule deactivated — Undo"). */
  undo?: { label: string; onClick: () => void };
}

const variantConfig: Record<B24NotificationVariant, { icon: LucideIcon; accent: string }> = {
  info: { icon: Info, accent: "bg-blue-500" },
  success: { icon: CheckCircle2, accent: "bg-green-500" },
  warning: { icon: AlertTriangle, accent: "bg-amber-500" },
  error: { icon: AlertCircle, accent: "bg-rose-500" },
};

/**
 * B24Notification — Native-style Bitrix24 rich notification.
 * Features dark aesthetic, progress indicator, and specialized icons.
 */
export const B24Notification: React.FC<B24NotificationProps> = ({
  title,
  message,
  variant = "info",
  icon,
  duration = 5000,
  onClose,
  className,
  undo,
}) => {
  const [progress, setProgress] = useState(100);
  const config = variantConfig[variant];
  const Icon = icon || config.icon;

  useEffect(() => {
    if (duration <= 0) return;

    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        onClose?.();
      }
    }, 10);

    return () => {
      clearInterval(timer);
    };
  }, [duration, onClose]);

  return (
    <div
      className={cn(
        "relative overflow-hidden w-[380px] rounded-xl bg-[#2b333c] text-white shadow-2xl animate-in slide-in-from-right-full duration-300",
        className,
      )}
    >
      <div className="flex p-5 gap-4">
        {/* Icon Section */}
        <div className="shrink-0 pt-1">
          <div
            className={cn(
              "size-6 flex items-center justify-center rounded-sm",
              variant === "warning" ? "text-amber-500" : "text-white/80",
            )}
          >
            <Icon className="size-5" />
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 pr-6">
          <h4 className="font-bold text-[15px] leading-tight mb-1">{title}</h4>
          <p className="text-[14px] text-white/80 leading-relaxed font-normal">{message}</p>
          {undo && (
            <button
              onClick={() => {
                undo.onClick();
                onClose?.();
              }}
              className="mt-2 text-[13px] font-semibold text-white underline underline-offset-2 hover:text-white/80 transition-colors"
            >
              {undo.label}
            </button>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Progress Bar */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 h-[3px] bg-white/10 w-full">
          <div
            className={cn("h-full transition-all duration-100 ease-linear", config.accent)}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};
